import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  persistVaultAuthMeTenants,
  persistVaultTenantFromTenantsPayload,
  shouldSendVaultXTenantHeader,
} from './vault-active-tenant.util';
import { isSimappeProductCompanyLabel } from './vault-nebula-company.util';
import { persistVaultTenantSnapshot } from './vault-tenant-snapshot.util';
import {
  normalizeVaultTenantId,
  persistVaultTenantScopeToStorage,
  isVaultUuid,
} from './vault-tenant-scope.util';
import {
  normalizeVaultTenantCatalog,
  persistVaultTenantCatalog,
  persistVaultTenantCatalogFromTenantRows,
  resolveVaultTenantNameFromCatalog,
  firstModulePermissionTenantHint,
} from './vault-tenant-catalog.util';
import {
  vaultCurrentTenantIdFromSession,
  vaultCurrentUserIdFromSession,
  vaultUserIsSuperadminFromSession,
} from './vault-user-roles.util';
import { readVaultDashboardRole, readVaultOperativeRole } from './vault-operative-role.util';
import { unwrapVaultApiArray } from './vault-api-unwrap.util';
import { VaultTenantSessionBootstrapService } from './vault-tenant-session-bootstrap.service';
import { isVaultAccountBlockedCircuitOpen } from './vault-account-blocked.util';
import {
  readStoredVaultTenantName,
  resolveVaultActiveTenantId,
} from './vault-active-tenant.util';

@Injectable({ providedIn: 'root' })
export class VaultOperativeRoleSyncService {
  private readonly http = inject(HttpClient);
  private readonly tenantBootstrap = inject(VaultTenantSessionBootstrapService);
  private syncInFlight: Observable<void> | null = null;
  private cachedSync$: Observable<void> | null = null;
  private cachedSyncAt = 0;
  private static readonly SYNC_TTL_MS = 15_000;

  /** Hidrata rol Vault desde `/api/auth/me` + `/api/tenants/my-tenants` + `/api/dashboard/stats`. */
  syncFromApi(options?: { force?: boolean }): Observable<void> {
    if (typeof window === 'undefined') return of(undefined);
    if (isVaultAccountBlockedCircuitOpen()) return of(undefined);

    const token = localStorage.getItem('access_token');
    if (!token) return of(undefined);

    const now = Date.now();
    if (
      !options?.force &&
      this.cachedSync$ &&
      now - this.cachedSyncAt < VaultOperativeRoleSyncService.SYNC_TTL_MS
    ) {
      return this.cachedSync$;
    }

    if (this.syncInFlight) {
      return this.syncInFlight;
    }

    this.syncInFlight = this.tenantBootstrap.ensureSession$(options).pipe(
      switchMap(() => {
        const tenantId = resolveVaultActiveTenantId();
        const hasVaultTenantName =
          !!resolveVaultTenantNameFromCatalog(tenantId) ||
          !!readStoredVaultTenantName(tenantId);
        if (hasVaultTenantName && tenantId) {
          return of(undefined);
        }

        const afterCatalog$ =
          tenantId && hasVaultTenantName
            ? of(undefined)
            : vaultUserIsSuperadminFromSession()
              ? this.loadVaultTenantCatalog(token)
              : this.loadMyTenantsForSession(token);

        return afterCatalog$.pipe(
          switchMap(() => {
            const resolvedId = normalizeVaultTenantId(resolveVaultActiveTenantId());
            const resolvedName =
              resolveVaultTenantNameFromCatalog(resolvedId) ||
              readStoredVaultTenantName(resolvedId);
            if (resolvedId && resolvedName) {
              return of(undefined);
            }
            if (!resolvedId) {
              return this.loadMyTenantsForSession(token);
            }
            if (resolvedId) {
              return this.loadTenantNameById(token, resolvedId).pipe(
                switchMap(() => {
                  const name =
                    resolveVaultTenantNameFromCatalog(resolvedId) ||
                    readStoredVaultTenantName(resolvedId);
                  return name
                    ? of(undefined)
                    : this.loadSessionTenantFromModulePermissions(token);
                }),
              );
            }
            return this.loadSessionTenantFromModulePermissions(token);
          }),
          map(() => undefined),
          catchError(() => of(undefined)),
        );
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
      finalize(() => {
        this.syncInFlight = null;
      }),
      shareReplay(1),
    );

    this.cachedSync$ = this.syncInFlight;
    this.cachedSyncAt = now;
    return this.syncInFlight;
  }

  clearSyncCache(): void {
    this.syncInFlight = null;
    this.cachedSync$ = null;
    this.cachedSyncAt = 0;
  }

  /** `GET /api/tenants/{id}` — usuarios con acceso al tenant (sin ser admin). */
  private loadTenantNameById(token: string, tenantId: string): Observable<void> {
    const id = normalizeVaultTenantId(tenantId);
    if (!id) return of(undefined);

    const { headers, params } = this.buildVaultAuthRequest(token);
    return this.http
      .get<unknown>(`/api/tenants/${encodeURIComponent(id)}`, {
        headers,
        ...(params.keys().length ? { params } : {}),
      })
      .pipe(
        tap((raw) => {
          const record =
            raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
          if (!record) return;
          const name = String(record['name'] ?? record['tenant_name'] ?? '').trim();
          if (!name || isSimappeProductCompanyLabel(name)) return;
          persistVaultTenantCatalog([{ id, name }]);
          const role =
            readVaultOperativeRole()?.trim() || readVaultDashboardRole()?.trim() || '';
          persistVaultAuthMeTenants([{ tenant_id: id, tenant_name: name, role }]);
          persistVaultTenantSnapshot({ tenant_id: id, tenant_name: name, role });
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  private loadSessionTenantFromModulePermissions(token: string): Observable<void> {
    const userId = vaultCurrentUserIdFromSession();
    if (!userId || !isVaultUuid(userId)) return of(undefined);

    const { headers, params } = this.buildVaultAuthRequest(token);
    return this.http
      .get<unknown>(`/api/module-permissions/user/${encodeURIComponent(userId)}`, {
        headers,
        ...(params.keys().length ? { params } : {}),
      })
      .pipe(
        tap((raw) => {
          const hint = firstModulePermissionTenantHint(raw);
          if (!hint?.tenant_id) return;
          persistVaultTenantScopeToStorage(hint.tenant_id);
          const role =
            readVaultOperativeRole()?.trim() || readVaultDashboardRole()?.trim() || '';
          persistVaultAuthMeTenants([
            {
              tenant_id: hint.tenant_id,
              tenant_name: hint.tenant_name,
              role,
            },
          ]);
          if (hint.tenant_name) {
            persistVaultTenantCatalog([{ id: hint.tenant_id, name: hint.tenant_name }]);
          }
          persistVaultTenantSnapshot({
            tenant_id: hint.tenant_id,
            tenant_name: hint.tenant_name,
            role,
          });
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  /** `GET /api/tenants/my-tenants` — usuarios sin acceso a admin. */
  private loadMyTenantsForSession(token: string): Observable<void> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get<unknown>('/api/tenants/my-tenants', { headers }).pipe(
      tap((raw) => {
        const list = unwrapVaultApiArray(raw);
        if (!list.length) return;
        persistVaultTenantFromTenantsPayload(list);
        persistVaultTenantCatalogFromTenantRows(list);
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  private loadVaultTenantCatalog(token: string): Observable<void> {
    if (!vaultUserIsSuperadminFromSession()) {
      return this.loadMyTenantsForSession(token);
    }
    const { headers, params } = this.buildVaultAuthRequest(token);
    return this.http
      .get<unknown>('/api/admin/tenants', {
        headers,
        ...(params.keys().length ? { params } : {}),
      })
      .pipe(
        tap((raw) => {
          const list = unwrapVaultApiArray(raw);
          const catalog = normalizeVaultTenantCatalog(list.length ? list : raw);
          persistVaultTenantCatalog(catalog);
          const tenantId = resolveVaultActiveTenantId();
          const name = resolveVaultTenantNameFromCatalog(tenantId);
          if (tenantId && name) {
            persistVaultAuthMeTenants([{ tenant_id: tenantId, tenant_name: name, role: '' }]);
            const role =
              readVaultOperativeRole()?.trim() || readVaultDashboardRole()?.trim() || '';
            persistVaultTenantSnapshot({ tenant_id: tenantId, tenant_name: name, role });
          }
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  private buildVaultAuthRequest(token: string): { headers: HttpHeaders; params: HttpParams } {
    const tenantId = normalizeVaultTenantId(vaultCurrentTenantIdFromSession());
    let headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    let params = new HttpParams();
    if (tenantId && shouldSendVaultXTenantHeader()) {
      headers = headers.set('X-Tenant-ID', tenantId);
      params = params.set('tenant_id', tenantId);
    }
    return { headers, params };
  }
}
